import { useEffect } from 'react';
import { Button } from '@/components/shared/Button';
import { Portal } from '@/components/shared/Portal';

interface ConfirmationDialogProps {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  cancelLabel?: string;
  secondaryActionLabel?: string;
  secondaryActionVariant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  confirmVariant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  confirmationLabel?: string;
  requiredText?: string;
  confirmationValue: string;
  isProcessing?: boolean;
  onConfirmationValueChange: (value: string) => void;
  onClose: () => void;
  onSecondaryAction?: () => void;
  onConfirm: () => void;
}

export function ConfirmationDialog({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Cancel',
  secondaryActionLabel,
  secondaryActionVariant = 'ghost',
  confirmVariant = 'danger',
  confirmationLabel,
  requiredText,
  confirmationValue,
  isProcessing = false,
  onConfirmationValueChange,
  onClose,
  onSecondaryAction,
  onConfirm,
}: ConfirmationDialogProps): JSX.Element | null {
  useEffect(() => {
    if (!open || isProcessing) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isProcessing, onClose, open]);

  if (!open) {
    return null;
  }

  const requiresTypedConfirmation = Boolean(requiredText);
  const isConfirmDisabled = isProcessing || (requiresTypedConfirmation && confirmationValue.trim() !== requiredText);

  return (
    <Portal>
      <div className="confirmation-dialog" role="dialog" aria-modal="true" aria-labelledby="confirmation-dialog-title">
        <div className="confirmation-dialog__backdrop" onClick={isProcessing ? undefined : onClose} />
        <div className="confirmation-dialog__panel">
          <div className="confirmation-dialog__content">
            <p className="confirmation-dialog__eyebrow">Confirmation Required</p>
            <h3 id="confirmation-dialog-title">{title}</h3>
            <p>{description}</p>

            {requiredText ? (
              <label className="field">
                <span className="field__label">{confirmationLabel ?? 'Type to confirm'}</span>
                <input
                  className="field__input"
                  value={confirmationValue}
                  onChange={(event) => onConfirmationValueChange(event.target.value)}
                  autoComplete="off"
                />
                <span className="form-footnote">Required text: {requiredText}</span>
              </label>
            ) : null}
          </div>

          <div className="confirmation-dialog__actions">
            <Button variant="secondary" onClick={onClose} disabled={isProcessing}>
              {cancelLabel}
            </Button>
            {secondaryActionLabel && onSecondaryAction ? (
              <Button variant={secondaryActionVariant} onClick={onSecondaryAction} disabled={isProcessing}>
                {secondaryActionLabel}
              </Button>
            ) : null}
            <Button variant={confirmVariant} onClick={onConfirm} disabled={isConfirmDisabled}>
              {isProcessing ? 'Working...' : confirmLabel}
            </Button>
          </div>
        </div>
      </div>
    </Portal>
  );
}
